import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { authService } from './services/authService';
import { Toaster } from './components/ui/toaster'; // Atualizado o caminho do Toaster

// Páginas
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Configuracoes from './pages/Configuracoes';
import RespostasRapidas from './pages/RespostasRapidas';
import Etiquetas from './pages/Etiquetas';
import Campanhas from './pages/Campanhas';
import Relatorios from './pages/Relatorios';
import PoliticaPrivacidade from './pages/PoliticaPrivacidade';
import TermosDeUso from './pages/TermosDeUso';
import DataDeletion from './pages/DataDeletion';
import MetaCallback from './pages/MetaCallback';
import AjudaMetaGrupos from './pages/AjudaMetaGrupos';
import KanbanPage from './pages/KanbanPage';
import GruposPage from './pages/GruposPage';
import PainelOperacoesPage from './pages/PainelOperacoesPage';
import PainelTvPage from './pages/PainelTvPage';
import DiagnosticoEntregaPage from './pages/DiagnosticoEntregaPage';
import DisparoAutomaticoPage from './pages/DisparoAutomaticoPage';
import LogsSistemaPage from './pages/LogsSistemaPage';
import CookieConsent from './components/CookieConsent';
import ReviewModeCaptions from './components/ReviewModeCaptions';

// Componente de rota protegida
const RotaProtegida = ({ children }: { children: React.ReactNode }) => {
  const [validating, setValidating] = React.useState(true);
  const [valido, setValido] = React.useState(false);

  React.useEffect(() => {
    if (!authService.isAuthenticated()) {
      setValido(false);
      setValidating(false);
      return;
    }

    authService.refresh()
      .then(() => {
        setValido(true);
        setValidating(false);
      })
      .catch(() => {
        authService.logout();
        setValido(false);
        setValidating(false);
      });
  }, []);

  if (validating) return null;
  return valido ? <>{children}</> : <Navigate to="/login" />;
};

// (Removed static user evaluation)

function App() {
  const lastActivityRef = useRef(Date.now());
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const IDLE_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds
  const REFRESH_TIME = 10 * 60 * 1000; // 10 minutes before expiry, but we'll use a shorter interval for demo

  const resetActivityTimer = () => {
    lastActivityRef.current = Date.now();
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    idleTimeoutRef.current = setTimeout(checkForRefresh, IDLE_TIME);
  };

  const checkForRefresh = async () => {
    if (authService.isAuthenticated()) {
      try {
        await authService.refresh();
        console.log('Token renovado automaticamente');
      } catch (error) {
        console.error('Erro ao renovar token:', error);
        // If refresh fails, user will be redirected on next API call via interceptor
      }
    }
  };

  useEffect(() => {
    const handleActivity = () => {
      resetActivityTimer();
    };

    // Listen for user activity
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);

    // Start initial timer
    resetActivityTimer();

    // Periodic refresh check (fallback)
    refreshTimeoutRef.current = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity >= IDLE_TIME && authService.isAuthenticated()) {
        checkForRefresh();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      if (refreshTimeoutRef.current) {
        clearInterval(refreshTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Toaster />
      <CookieConsent />
      <ReviewModeCaptions />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/configuracoes"
            element={
              <RotaProtegida>
                <Configuracoes />
              </RotaProtegida>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RotaProtegida>
                <Dashboard />
              </RotaProtegida>
            }
          />
          <Route
            path="/atendimento"
            element={
              <RotaProtegida>
                <Navigate to="/dashboard" replace />
              </RotaProtegida>
            }
          />
          <Route
            path="/respostas-rapidas"
            element={
              <RotaProtegida>
                <RespostasRapidas />
              </RotaProtegida>
            }
          />
          <Route
            path="/etiquetas"
            element={
              <RotaProtegida>
                <Etiquetas />
              </RotaProtegida>
            }
          />
          <Route
            path="/campanhas"
            element={
              <RotaProtegida>
                <Campanhas />
              </RotaProtegida>
            }
          />
          <Route
            path="/relatorios"
            element={
              <RotaProtegida>
                <Relatorios />
              </RotaProtegida>
            }
          />
          <Route
            path="/kanban"
            element={
              <RotaProtegida>
                <KanbanPage />
              </RotaProtegida>
            }
          />
          <Route
            path="/painel-operacional"
            element={
              <RotaProtegida>
                <PainelOperacoesPage />
              </RotaProtegida>
            }
          />
          <Route
            path="/logs"
            element={
              <RotaProtegida>
                <LogsSistemaPage />
              </RotaProtegida>
            }
          />
          <Route
            path="/diagnostico-entrega"
            element={
              <RotaProtegida>
                <DiagnosticoEntregaPage />
              </RotaProtegida>
            }
          />
          <Route
            path="/disparo-automatico"
            element={
              <RotaProtegida>
                <DisparoAutomaticoPage />
              </RotaProtegida>
            }
          />
          <Route
            path="/grupos"
            element={
              <RotaProtegida>
                <GruposPage />
              </RotaProtegida>
            }
          />
          {/* Redirecionamento baseado no papel do usuário */}
          <Route
            path="/"
            element={
              <RotaProtegida>
                {(() => {
                  const currentUser = authService.getCurrentUser();
                  return currentUser?.role === 'SuperAdmin' ? (
                    <Navigate to="/configuracoes" />
                  ) : (
                    <Navigate to="/dashboard" />
                  );
                })()}
              </RotaProtegida>
            }
          />
          {/* Rotas publicas - paginas legais */}
          <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/termos-de-uso" element={<TermosDeUso />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          {/* Ajuda: liberar Groups API (Conta Comercial Oficial) — aberta em nova aba pelo erro da tela de Grupos */}
          <Route path="/ajuda/meta-grupos" element={<AjudaMetaGrupos />} />
          {/* Retorno do OAuth da Meta (pode cair aqui sem opener, ex.: aba própria).
              Rota pública: não passa por RotaProtegida nem pelo catch-all. */}
          <Route path="/meta-callback" element={<MetaCallback />} />
          {/* Painel TV: público, acesso pelo token da URL (gerado em Configurações → Painel TV) */}
          <Route path="/painel-tv/:token" element={<PainelTvPage />} />

          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;
